import { useMemo, useState } from "react";
import { Search, AlertTriangle, SearchX, Sparkle, MousePointerClick, Bookmark, SlidersHorizontal, ArrowUpDown, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ItemRow } from "@/components/ItemRow";
import { DetailPane } from "@/components/DetailPane";
import { EmptyState } from "@/components/EmptyState";
import { useItemsStore } from "@/lib/stores/itemsStore";
import { useIsMobile } from "@/hooks/use-mobile";
import { searchContent } from "@/lib/data/items";
import { searchSuggestions, recentSearches } from "@/lib/data/extras";

const PLATFORMS = ["YouTube", "X", "Web", "TikTok"];

export default function Buscar() {
  const { items, selectedId, select, detailOpen, closeDetail } = useItemsStore();
  const isMobile = useIsMobile();
  const [query, setQuery] = useState("");
  const [state, setState] = useState("idle");
  const [results, setResults] = useState([]);
  const [platformTab, setPlatformTab] = useState("Todos");

  const selected = items.find((i) => i.id === selectedId);

  const run = (q) => {
    if (!q) return;
    setQuery(q);
    setState("loading");
    setPlatformTab("Todos");
    setTimeout(() => {
      if (q.toLowerCase() === "error") {
        setState("error");
        return;
      }
      const found = searchContent(items, q);
      setResults(found);
      setState("done");
      if (!isMobile && found.length > 0) select(found[0].id);
    }, 900);
  };

  const submit = (e) => {
    e?.preventDefault();
    run(query.trim());
  };

  const visible = useMemo(
    () => (platformTab === "Todos" ? results : results.filter((i) => i.platform === platformTab)),
    [results, platformTab],
  );

  const countFor = (p) => results.filter((i) => i.platform === p).length;

  return (
    <div className="h-full flex flex-col min-h-0" data-testid="search-page">
      {/* Cabecera de búsqueda */}
      <div className="pt-5 pb-4 border-b shrink-0">
        <div className="max-w-[1400px] mx-auto w-full px-4 md:px-8 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl tracking-tight">Búsqueda</h1>
            <p className="mt-1 text-sm text-muted-foreground flex items-center gap-1.5">
              Búsqueda semántica <Sparkle className="h-3.5 w-3.5 text-primary/70" strokeWidth={1.75} /> — encuentra por significado, no por palabras exactas.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => toast("Podrás guardar búsquedas en la versión final.")}>
            <Bookmark className="h-3.5 w-3.5" strokeWidth={1.75} /> Guardar búsqueda
          </Button>
        </div>

        <div className="rounded-xl border bg-card p-3 md:p-4 space-y-3">
          <form onSubmit={submit} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" strokeWidth={1.75} />
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="aquel vídeo donde explicaban automatizar facturas…"
                className="pl-11 h-11 text-[15px]"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" className="h-11 px-5" data-testid="search-submit-button">
              Buscar
              <kbd className="hidden sm:inline-block rounded border border-primary-foreground/30 px-1.5 text-[11px] font-normal">↵ Enter</kbd>
            </Button>
          </form>
          <div className="flex items-center gap-2 flex-wrap text-[13px]">
            <span className="text-muted-foreground">Prueba con:</span>
            {searchSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => run(s)}
                className="rounded-full border bg-background px-3 py-1 text-muted-foreground hover:text-foreground hover:border-teal-300/60 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {state === "done" && (
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin">
            <button
              type="button"
              onClick={() => setPlatformTab("Todos")}
              className={cn(
                "rounded-md px-3 py-1.5 text-[13px] font-medium whitespace-nowrap transition-colors",
                platformTab === "Todos" ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              Todos <span className="ml-1 tabular-nums">{results.length}</span>
            </button>
            {PLATFORMS.filter((p) => countFor(p) > 0).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPlatformTab(p)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-[13px] font-medium whitespace-nowrap transition-colors",
                  platformTab === p ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {p} <span className="ml-1 tabular-nums text-muted-foreground">{countFor(p)}</span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => toast("Más filtros en la versión final.")}
              className="inline-flex items-center gap-1.5 rounded-md border bg-card px-2.5 py-1.5 text-[13px] text-muted-foreground hover:text-foreground whitespace-nowrap"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" strokeWidth={1.75} /> Más filtros
            </button>
            <span className="flex-1" />
            <span className="text-[13px] text-muted-foreground tabular-nums whitespace-nowrap">{visible.length} resultados</span>
            <button
              type="button"
              onClick={() => toast("Orden configurable en la versión final.")}
              className="hidden lg:inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground whitespace-nowrap"
            >
              <ArrowUpDown className="h-3.5 w-3.5" strokeWidth={1.75} /> Ordenar: Relevancia
              <ChevronDown className="h-3.5 w-3.5 opacity-60" strokeWidth={1.75} />
            </button>
          </div>
        )}
        </div>
      </div>

      {/* Resultados + detalle */}
      <div className="flex-1 flex min-h-0 max-w-[1400px] mx-auto w-full md:px-8">
        <div className="w-full md:w-[420px] lg:w-[460px] md:border-r flex flex-col min-h-0 shrink-0">
          <div className="flex-1 overflow-y-auto scrollbar-thin px-2 py-3 space-y-0.5">
            {state === "idle" && (
              <div className="px-2">
                <EmptyState
                  icon={Search}
                  title="Pregunta por lo que recuerdas"
                  hint="Por ejemplo: «vídeos de n8n», «inspiración UI», «RAG barato»."
                  testId="search-idle"
                />
                <div className="px-4">
                  <p className="text-xs font-semibold text-muted-foreground mb-2">Búsquedas recientes</p>
                  <div className="space-y-1">
                    {recentSearches.slice(0, 4).map((q) => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => run(q)}
                        className="block w-full text-left rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/70 truncate transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {state === "loading" && (
              <div className="px-2 space-y-2" data-testid="search-loading">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-[84px] rounded-lg" />
                ))}
              </div>
            )}
            {state === "error" && (
              <EmptyState icon={AlertTriangle} title="Algo ha fallado al buscar." hint="Tu biblioteca sigue intacta. Vuelve a intentarlo." testId="search-error">
                <Button variant="outline" size="sm" onClick={submit} data-testid="search-retry-button">
                  Reintentar
                </Button>
              </EmptyState>
            )}
            {state === "done" && visible.length === 0 && (
              <EmptyState
                icon={SearchX}
                title={`Sin resultados para «${query.trim()}».`}
                hint="Prueba con otras palabras o comparte contenido nuevo."
                testId="search-no-results"
              />
            )}
            {state === "done" &&
              visible.map((item) => (
                <ItemRow key={item.id} item={item} selected={item.id === selectedId} onClick={() => select(item.id)} />
              ))}
          </div>
        </div>

        <div className="hidden md:flex flex-1 min-w-0 min-h-0">
          {selected ? (
            <div className="flex-1 min-w-0">
              <DetailPane item={selected} onClose={() => select(null)} />
            </div>
          ) : (
            <div className="flex-1 grid place-items-center">
              <EmptyState icon={MousePointerClick} title="Selecciona un contenido para ver su detalle" />
            </div>
          )}
        </div>
      </div>

      {isMobile && selected && detailOpen && (
        <div className="fixed inset-0 z-50 bg-background md:hidden">
          <DetailPane item={selected} onBack={closeDetail} />
        </div>
      )}
    </div>
  );
}
